import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import { REHYDRATE } from "redux-persist";
import store from "../../state/Store";
import AccessLog from "../../types/system/AccessLog";
import PageSpecification from "../../types/system/PageSpecification";

// Define the type for our state store entry
type AccessLogState = {
    allData: Record<number, AccessLog[]>;
    pageData: AccessLog[];
    virtualSpec: PageSpecification;
    internalSpec: PageSpecification;
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: AccessLogState = {
    allData: {},
    pageData: [],
    virtualSpec: {
        page: 0,
        size: 20,
        total: 0,
    } as PageSpecification,
    internalSpec: {
        page: -1,
        size: 200,
        total: 0,
    } as PageSpecification,
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const accessLogSlice = createSlice({
    name: "accessLog",
    initialState: initialState,
    reducers: {
        setVirtualPageAndSize: (
            state,
            action: PayloadAction<PageSpecification>,
        ) => {
            state.virtualSpec.page = action.payload.page;
            state.virtualSpec.size = action.payload.size;

            const internalPageIndex = Math.floor(
                (state.virtualSpec.page * state.virtualSpec.size) /
                    state.internalSpec.size,
            );
            const internalPage = state.allData[internalPageIndex];
            const translatedPageStart =
                state.virtualSpec.page * state.virtualSpec.size -
                internalPageIndex * state.internalSpec.size;
            const translatedPageEnd =
                translatedPageStart + state.virtualSpec.size;

            state.pageData =
                internalPage?.slice(
                    Math.min(translatedPageStart, internalPage.length),
                    Math.min(translatedPageEnd, internalPage.length),
                ) ?? [];

            state.virtualSpec.waiting =
                state.virtualSpec.loading && state.pageData.length === 0;

            while (state.pageData.length < state.virtualSpec.size)
                state.pageData.push(new AccessLog());
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const allData = {};

                if (rawData?.payload?.accessLog?.allData)
                    for (let entry in rawData?.payload?.accessLog.allData)
                        allData[entry] = rawData?.payload?.accessLog.allData[
                            entry
                        ].map(item => new AccessLog(item));

                return {
                    allData: allData,
                    pageData:
                        rawData?.payload?.accessLog?.pageData?.map(
                            item => new AccessLog(item),
                        ) ?? [],
                    virtualSpec:
                        rawData?.payload?.accessLog?.virtualSpec ??
                        ({
                            page: 0,
                            size: 20,
                            total: 0,
                        } as PageSpecification),
                    internalSpec:
                        rawData?.payload?.accessLog?.internalSpec ??
                        ({
                            page: -1,
                            size: 200,
                            total: 0,
                        } as PageSpecification),
                    status: !rawData?.payload?.accessLog?.pageData?.length
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchAccessLogPage.pending, (state, action) => {
                state.status = "loading";
                state.virtualSpec.loading = true;
                state.internalSpec.loading = true;
            })
            .addCase(fetchAccessLogPage.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.virtualSpec.total = action.payload[1];
                state.internalSpec.total = action.payload[1];
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.allData[action.payload[0]] = action.payload[2].map(
                    item => new AccessLog(item),
                );

                const internalPageIndex = Math.floor(
                    (state.virtualSpec.page * state.virtualSpec.size) /
                        state.internalSpec.size,
                );
                const internalPage = state.allData[internalPageIndex];
                const translatedPageStart =
                    state.virtualSpec.page * state.virtualSpec.size -
                    internalPageIndex * state.internalSpec.size;
                const translatedPageEnd =
                    translatedPageStart + state.virtualSpec.size;

                state.pageData =
                    internalPage?.slice(
                        Math.min(translatedPageStart, internalPage.length),
                        Math.min(translatedPageEnd, internalPage.length),
                    ) ?? [];

                state.virtualSpec.waiting =
                    state.virtualSpec.loading && state.pageData.length === 0;

                while (state.pageData.length < state.virtualSpec.size)
                    state.pageData.push(new AccessLog());
            })
            .addCase(fetchAccessLogPage.rejected, (state, action) => {
                state.status = "failed";
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.error = action.error.message;
            });
    },
});

const { setVirtualPageAndSize } = accessLogSlice.actions;

// This is a thunk, they are used to load the state
const fetchAccessLogPage = createAsyncThunk(
    "accessLog/fetchAccessLogPage",
    async (fetchIndex: number) => {
        const { accessLog } = store.getState().db;
        const response = await Promise.all([
            Datastore.getType<number>(`api/Logs/Access/Count`),
            Datastore.getType<AccessLog[]>(
                `api/Logs/Access/Page/${fetchIndex}/Size/${accessLog.internalSpec.size}`,
            ),
        ]);
        const result = [fetchIndex, response[0], response[1]] as [
            number,
            number,
            AccessLog[],
        ];
        return result;
    },
);
const evaluatePrivateData = createAsyncThunk(
    "accessLog/evaluatePrivateData",
    async (specification: PageSpecification, thunkAPI): Promise<void> => {
        const { accessLog } = store.getState().db;
        const requiredInternalPage = Math.max(
            Math.ceil(
                ((specification.page + 1) * specification.size - 1) /
                    accessLog.internalSpec.size,
            ) - 1,
            0,
        );

        if (accessLog.status !== "loading") {
            if (!accessLog.allData[requiredInternalPage]) {
                await thunkAPI.dispatch(
                    fetchAccessLogPage(requiredInternalPage),
                );
            } else {
                const direction =
                    specification.page > accessLog.virtualSpec.page
                        ? 1
                        : specification.page < accessLog.virtualSpec.page
                        ? -1
                        : 0;

                if (direction !== 0) {
                    const pageGap =
                        direction > 0
                            ? (accessLog.internalSpec.size -
                                  (specification.page * specification.size -
                                      requiredInternalPage *
                                          accessLog.internalSpec.size)) /
                              specification.size
                            : (specification.page * specification.size -
                                  requiredInternalPage *
                                      accessLog.internalSpec.size) /
                              specification.size;

                    if (pageGap < 4) {
                        if (
                            direction < 0 &&
                            requiredInternalPage > 0 &&
                            !accessLog.allData[requiredInternalPage - 1]
                        ) {
                            await thunkAPI.dispatch(
                                fetchAccessLogPage(requiredInternalPage - 1),
                            );
                        } else if (
                            direction > 0 &&
                            !accessLog.allData[requiredInternalPage + 1]
                        ) {
                            await thunkAPI.dispatch(
                                fetchAccessLogPage(requiredInternalPage + 1),
                            );
                        }
                    }
                }
            }
        }
    },
);
export const getAccessLogPage = createAsyncThunk(
    "accessLog/getAccessLogPage",
    async (_, thunkAPI): Promise<void> => {
        const { accessLog } = store.getState().db;
        thunkAPI.dispatch(evaluatePrivateData(accessLog.virtualSpec));
    },
);
export const setAccessLogPageAndSize = createAsyncThunk(
    "accessLog/setAccessLogPageAndSize",
    async (specification: PageSpecification, thunkAPI) => {
        thunkAPI.dispatch(evaluatePrivateData(specification));
        thunkAPI.dispatch(setVirtualPageAndSize(specification));
    },
);

// We have to export the reducer as this is added to the state store
export default accessLogSlice.reducer;
